import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ClipboardList, Lock } from 'lucide-react';
import { Actions as PAYMENT_ACTIONS } from '../../../store/actions/paymentActions';
import { hasFeatureEntitlement } from '../../utils/subscriptionAccess';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

const FEATURE_KEY = 'WFH_SURVEY';

/**
 * Prevent direct Survey routes from bypassing resolved backend entitlements.
 * If access is unavailable, direct the user to the matching add-on or a paid plan.
 */
const SurveyEntitlementGate = ({
  children,
  orgDetails,
  error,
  catalog,
  catalogLoading,
  getAddonCatalog,
}) => {
  const canUseSurvey = hasFeatureEntitlement(orgDetails, FEATURE_KEY);
  const entitlementError = error?.code === 'feature_not_entitled' && error?.featureKey === FEATURE_KEY;
  const blocked = !canUseSurvey || entitlementError;
  const hasActivePaidSubscription =
    orgDetails?.hasSubscriptionActive === true && orgDetails?.subscriptionStatus !== 'trialing';
  const surveyProduct = useMemo(
    () => (catalog || []).find((product) => (product.feature_keys || []).includes(FEATURE_KEY)),
    [catalog],
  );

  useEffect(() => {
    if (blocked && !catalog?.length) getAddonCatalog();
  }, [blocked, catalog?.length, getAddonCatalog]);

  if (!blocked) return children;

  return (
    <div className='flex min-h-[60vh] items-center justify-center p-6'>
      <Card className='w-full max-w-xl border-border bg-card text-card-foreground'>
        <CardHeader>
          <div className='mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground'>
            <Lock size={20} strokeWidth={1.5} />
          </div>
          <CardTitle>WFH Survey is not included in your current access</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Your existing plan features stay unchanged. Add the Survey product to unlock WFH and commuting campaigns.
          </p>
          {surveyProduct && (
            <div className='rounded-lg border border-border bg-muted/30 p-4'>
              <div className='flex items-start gap-3'>
                <ClipboardList size={18} strokeWidth={1.5} className='mt-0.5 text-primary' />
                <div>
                  <p className='font-semibold text-foreground'>{surveyProduct.name}</p>
                  {surveyProduct.price_display && (
                    <p className='mt-1 text-sm text-muted-foreground'>{surveyProduct.price_display}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className='flex flex-wrap gap-2'>
            {hasActivePaidSubscription ? (
              <Button asChild>
                <Link to='/addons'>{catalogLoading ? 'Loading add-ons…' : 'View Survey add-on'}</Link>
              </Button>
            ) : (
              <Button asChild>
                <Link to='/plans'>Choose a plan</Link>
              </Button>
            )}
            <Button asChild variant='outline'>
              <Link to='/'>Return to dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  orgDetails: state.organisation?.details,
  error: state.survey?.error,
  catalog: state.payment?.addonCatalog || [],
  catalogLoading: state.payment?.addonCatalogLoading || false,
});

const mapDispatchToProps = (dispatch) => ({
  getAddonCatalog: () => dispatch(PAYMENT_ACTIONS.getAddonCatalog()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyEntitlementGate);
